/**
 * Domain entities. These mirror SRS §6 (Data Model) but use camelCase and
 * integer minor units for money — see `@/domain/money`.
 */

export const SPLIT_TYPES = ['equal', 'exact', 'percentage', 'shares', 'custom'] as const;
export type SplitType = (typeof SPLIT_TYPES)[number];

export const SPLIT_TYPE_LABELS: Record<SplitType, string> = {
  equal: 'Equally',
  exact: 'Exact amounts',
  percentage: 'By percentage',
  shares: 'By shares',
  custom: 'Custom',
};

export const SPLIT_TYPE_HINTS: Record<SplitType, string> = {
  equal: 'Everyone selected owes the same amount.',
  exact: 'Enter what each person owes. Must add up to the total.',
  percentage: 'Enter each share as a percentage. Must add up to 100%.',
  shares: 'Enter relative shares (e.g. 2 for a couple, 1 for a single).',
  custom: 'Enter any amounts you like. Must add up to the total.',
};

export type Group = {
  id: string;
  name: string;
  currency: string;
  color: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Member = {
  id: string;
  groupId: string;
  name: string;
  phone: string | null;
  avatar: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Category = {
  id: string;
  name: string;
  /** Icon key, resolved by whichever client is rendering it. */
  icon: string;
  /** Hex colour, e.g. `#0EA5E9`. */
  color: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Expense = {
  id: string;
  groupId: string;
  title: string;
  categoryId: string | null;
  /** Total, in minor units (cents/paise). */
  amountMinor: number;
  paidBy: string;
  /** ISO date, `yyyy-MM-dd`. */
  date: string;
  notes: string | null;
  splitType: SplitType;
  createdAt: string;
  updatedAt: string;
};

export type ExpenseParticipant = {
  id: string;
  expenseId: string;
  memberId: string;
  /** What this member owes for the expense, in minor units. */
  shareAmountMinor: number;
  /**
   * Raw user input that produced `shareAmountMinor` — a percentage for
   * `percentage` splits, a share count for `shares` splits. Kept so the edit
   * screen can restore exactly what was typed instead of back-solving it.
   */
  weight: number | null;
};

export type Settlement = {
  id: string;
  groupId: string;
  fromMemberId: string;
  toMemberId: string;
  amountMinor: number;
  /** ISO date, `yyyy-MM-dd`. */
  date: string;
  note: string | null;
  createdAt: string;
};

/** An expense joined with the rows needed to render it in a list. */
export type ExpenseWithDetails = Expense & {
  participants: ExpenseParticipant[];
  categoryName: string | null;
  categoryIcon: string | null;
  categoryColor: string | null;
  paidByName: string;
};

/** Net position of a single member inside one group. */
export type MemberBalance = {
  memberId: string;
  memberName: string;
  isActive: boolean;
  /** Total the member paid out, minor units. */
  paidMinor: number;
  /** Total the member is responsible for, minor units. */
  owedMinor: number;
  /** Settlements sent minus settlements received, minor units. */
  settledMinor: number;
  /** Positive = is owed money, negative = owes money. */
  netMinor: number;
};

/** One suggested payment in a settlement plan. */
export type SettlementSuggestion = {
  fromMemberId: string;
  fromMemberName: string;
  toMemberId: string;
  toMemberName: string;
  amountMinor: number;
};
